/**
 * Engineering Ladder Templates
 * Pre-built career frameworks based on industry standards
 */

import type { LadderTemplate, Competency, Level, CompetencyLevel } from './types';

// Simple ID generator that works in both browser and server
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// ============================================================================
// Shared Competencies
// ============================================================================

const STANDARD_COMPETENCIES: Competency[] = [
  // Technical
  {
    id: 'tech-coding',
    name: 'Coding & Implementation',
    description: 'Ability to write clean, efficient, and maintainable code',
    category: 'technical',
    examples: [
      'Writes readable, well-structured code',
      'Applies appropriate design patterns',
      'Considers performance implications',
      'Writes comprehensive tests',
    ],
  },
  {
    id: 'tech-architecture',
    name: 'System Design & Architecture',
    description: 'Ability to design scalable and maintainable systems',
    category: 'technical',
    examples: [
      'Designs component interfaces',
      'Makes appropriate technology choices',
      'Considers scalability and reliability',
      'Documents architectural decisions',
    ],
  },
  {
    id: 'tech-debugging',
    name: 'Debugging & Problem Solving',
    description: 'Ability to diagnose and resolve complex technical issues',
    category: 'technical',
    examples: [
      'Uses systematic debugging approaches',
      'Identifies root causes efficiently',
      'Prevents issue recurrence',
      'Shares debugging knowledge',
    ],
  },
  // Leadership
  {
    id: 'lead-mentoring',
    name: 'Mentoring & Coaching',
    description: 'Ability to develop and grow other engineers',
    category: 'leadership',
    examples: [
      'Provides constructive code reviews',
      'Shares knowledge proactively',
      'Helps teammates grow skills',
      'Creates learning opportunities',
    ],
  },
  {
    id: 'lead-influence',
    name: 'Technical Influence',
    description: 'Ability to shape technical direction and decisions',
    category: 'leadership',
    examples: [
      'Drives technical decisions',
      'Builds consensus on approaches',
      'Champions best practices',
      'Influences beyond immediate team',
    ],
  },
  {
    id: 'lead-people',
    name: 'People Management',
    description: 'Ability to manage, develop, and retain team members',
    category: 'leadership',
    examples: [
      'Conducts effective 1:1s',
      'Provides regular feedback',
      'Supports career development',
      'Builds inclusive teams',
    ],
  },
  // Scope
  {
    id: 'scope-autonomy',
    name: 'Autonomy & Ownership',
    description: 'Level of independence and ownership over work',
    category: 'scope',
    examples: [
      'Works independently on tasks',
      'Owns features end-to-end',
      'Drives projects proactively',
      'Takes accountability for outcomes',
    ],
  },
  {
    id: 'scope-complexity',
    name: 'Complexity Management',
    description: 'Ability to handle increasingly complex work',
    category: 'scope',
    examples: [
      'Breaks down complex problems',
      'Manages dependencies effectively',
      'Handles ambiguity well',
      'Navigates organizational complexity',
    ],
  },
  // Impact
  {
    id: 'impact-delivery',
    name: 'Delivery & Execution',
    description: 'Ability to deliver high-quality work on time',
    category: 'impact',
    examples: [
      'Meets commitments consistently',
      'Delivers iteratively',
      'Balances speed and quality',
      'Manages scope effectively',
    ],
  },
  {
    id: 'impact-business',
    name: 'Business Impact',
    description: 'Ability to drive meaningful business outcomes',
    category: 'impact',
    examples: [
      'Understands business context',
      'Prioritizes based on impact',
      'Measures outcomes',
      'Connects technical work to business goals',
    ],
  },
  // Communication
  {
    id: 'comm-written',
    name: 'Written Communication',
    description: 'Ability to communicate effectively in writing',
    category: 'communication',
    examples: [
      'Writes clear documentation',
      'Crafts effective proposals',
      'Creates understandable diagrams',
      'Communicates status clearly',
    ],
  },
  {
    id: 'comm-verbal',
    name: 'Verbal Communication',
    description: 'Ability to communicate effectively in person',
    category: 'communication',
    examples: [
      'Presents ideas clearly',
      'Facilitates productive discussions',
      'Adapts to audience',
      'Handles difficult conversations',
    ],
  },
];

// ============================================================================
// Helper Functions
// ============================================================================

function createLevelCompetency(
  competencyId: string,
  level: CompetencyLevel,
  description?: string
) {
  return { competencyId, expectedLevel: level, description };
}

// ============================================================================
// Spotify-Style Template
// ============================================================================

const SPOTIFY_IC_LEVELS: Level[] = [
  {
    id: generateId(),
    code: 'L1',
    title: 'Junior Engineer',
    track: 'ic',
    order: 1,
    typicalYearsExperience: '0-2',
    scope: 'Task-level',
    impact: 'Individual tasks',
    responsibilities: [
      'Learn the codebase and development practices',
      'Complete well-defined tasks with guidance',
      'Ask questions and seek feedback actively',
      'Write basic tests for their code',
    ],
    progressionCriteria: [
      'Consistently delivers tasks on time',
      'Code requires minimal revisions in review',
      'Shows initiative in learning',
      'Contributes to team discussions',
    ],
    competencies: [
      createLevelCompetency('tech-coding', 'learning'),
      createLevelCompetency('tech-architecture', 'learning'),
      createLevelCompetency('tech-debugging', 'learning'),
      createLevelCompetency('lead-mentoring', 'learning'),
      createLevelCompetency('scope-autonomy', 'learning'),
      createLevelCompetency('impact-delivery', 'learning'),
      createLevelCompetency('comm-written', 'learning'),
    ],
  },
  {
    id: generateId(),
    code: 'L2',
    title: 'Engineer',
    track: 'ic',
    order: 2,
    typicalYearsExperience: '2-4',
    scope: 'Feature-level',
    impact: 'Team features',
    responsibilities: [
      'Deliver features independently',
      'Write clean, well-tested code',
      'Participate actively in code reviews',
      'Support on-call responsibilities',
    ],
    progressionCriteria: [
      'Owns features from design to deployment',
      'Provides valuable code review feedback',
      'Debugs complex issues independently',
      'Contributes to technical documentation',
    ],
    competencies: [
      createLevelCompetency('tech-coding', 'practicing'),
      createLevelCompetency('tech-architecture', 'learning'),
      createLevelCompetency('tech-debugging', 'practicing'),
      createLevelCompetency('lead-mentoring', 'learning'),
      createLevelCompetency('scope-autonomy', 'practicing'),
      createLevelCompetency('impact-delivery', 'practicing'),
      createLevelCompetency('comm-written', 'practicing'),
    ],
  },
  {
    id: generateId(),
    code: 'L3',
    title: 'Senior Engineer',
    track: 'ic',
    order: 3,
    typicalYearsExperience: '4-7',
    scope: 'Project-level',
    impact: 'Team/squad outcomes',
    responsibilities: [
      'Lead technical projects end-to-end',
      'Design and review architecture decisions',
      'Mentor junior team members',
      'Drive technical quality improvements',
    ],
    progressionCriteria: [
      'Leads complex projects successfully',
      'Improves team processes and practices',
      'Mentees show clear growth',
      'Influences technical direction',
    ],
    competencies: [
      createLevelCompetency('tech-coding', 'leading'),
      createLevelCompetency('tech-architecture', 'practicing'),
      createLevelCompetency('tech-debugging', 'leading'),
      createLevelCompetency('lead-mentoring', 'practicing'),
      createLevelCompetency('lead-influence', 'practicing'),
      createLevelCompetency('scope-autonomy', 'leading'),
      createLevelCompetency('scope-complexity', 'practicing'),
      createLevelCompetency('impact-delivery', 'leading'),
      createLevelCompetency('impact-business', 'practicing'),
      createLevelCompetency('comm-written', 'leading'),
      createLevelCompetency('comm-verbal', 'practicing'),
    ],
  },
  {
    id: generateId(),
    code: 'L4',
    title: 'Staff Engineer',
    track: 'ic',
    order: 4,
    typicalYearsExperience: '7-10',
    scope: 'Multi-team',
    impact: 'Tribe/department outcomes',
    responsibilities: [
      'Drive technical strategy across teams',
      'Solve cross-cutting technical challenges',
      'Build and scale engineering practices',
      'Represent engineering in strategic discussions',
    ],
    progressionCriteria: [
      'Technical vision adopted across teams',
      'Solves organization-level challenges',
      'Builds capabilities in multiple teams',
      'Recognized as domain expert',
    ],
    competencies: [
      createLevelCompetency('tech-coding', 'leading'),
      createLevelCompetency('tech-architecture', 'leading'),
      createLevelCompetency('tech-debugging', 'leading'),
      createLevelCompetency('lead-mentoring', 'leading'),
      createLevelCompetency('lead-influence', 'leading'),
      createLevelCompetency('scope-autonomy', 'leading'),
      createLevelCompetency('scope-complexity', 'leading'),
      createLevelCompetency('impact-delivery', 'leading'),
      createLevelCompetency('impact-business', 'leading'),
      createLevelCompetency('comm-written', 'leading'),
      createLevelCompetency('comm-verbal', 'leading'),
    ],
  },
  {
    id: generateId(),
    code: 'L5',
    title: 'Principal Engineer',
    track: 'ic',
    order: 5,
    typicalYearsExperience: '10+',
    scope: 'Organization-wide',
    impact: 'Company technical direction',
    responsibilities: [
      'Set company-wide technical direction',
      'Solve the hardest technical problems',
      'Build long-term technical strategy',
      'Represent company externally',
    ],
    progressionCriteria: [
      'Shapes company technical strategy',
      'Industry recognition for expertise',
      'Creates significant competitive advantage',
      'Attracts and retains top talent',
    ],
    competencies: [
      createLevelCompetency('tech-coding', 'defining'),
      createLevelCompetency('tech-architecture', 'defining'),
      createLevelCompetency('tech-debugging', 'defining'),
      createLevelCompetency('lead-mentoring', 'defining'),
      createLevelCompetency('lead-influence', 'defining'),
      createLevelCompetency('scope-autonomy', 'defining'),
      createLevelCompetency('scope-complexity', 'defining'),
      createLevelCompetency('impact-delivery', 'defining'),
      createLevelCompetency('impact-business', 'defining'),
      createLevelCompetency('comm-written', 'defining'),
      createLevelCompetency('comm-verbal', 'defining'),
    ],
  },
];

const SPOTIFY_MANAGEMENT_LEVELS: Level[] = [
  {
    id: generateId(),
    code: 'M1',
    title: 'Engineering Manager',
    track: 'management',
    order: 1,
    typicalYearsExperience: '5+',
    scope: 'Single team',
    impact: 'Team outcomes',
    responsibilities: [
      'Manage team of 4-8 engineers',
      'Conduct regular 1:1s and career conversations',
      'Drive team delivery and quality',
      'Partner with product on roadmap',
    ],
    progressionCriteria: [
      'Team consistently delivers on commitments',
      'Team members show career growth',
      'High retention and engagement',
      'Effective cross-team collaboration',
    ],
    competencies: [
      createLevelCompetency('tech-architecture', 'practicing'),
      createLevelCompetency('lead-mentoring', 'leading'),
      createLevelCompetency('lead-influence', 'practicing'),
      createLevelCompetency('lead-people', 'practicing'),
      createLevelCompetency('scope-complexity', 'practicing'),
      createLevelCompetency('impact-delivery', 'leading'),
      createLevelCompetency('impact-business', 'practicing'),
      createLevelCompetency('comm-written', 'leading'),
      createLevelCompetency('comm-verbal', 'leading'),
    ],
  },
  {
    id: generateId(),
    code: 'M2',
    title: 'Senior Engineering Manager',
    track: 'management',
    order: 2,
    typicalYearsExperience: '7+',
    scope: 'Multiple teams',
    impact: 'Tribe outcomes',
    responsibilities: [
      'Manage 2-3 teams or team of managers',
      'Drive technical strategy for area',
      'Build and evolve team structure',
      'Develop engineering managers',
    ],
    progressionCriteria: [
      'Teams exceed delivery expectations',
      'Develops multiple high-performing managers',
      'Drives significant process improvements',
      'Strong partnership with product leadership',
    ],
    competencies: [
      createLevelCompetency('tech-architecture', 'leading'),
      createLevelCompetency('lead-mentoring', 'leading'),
      createLevelCompetency('lead-influence', 'leading'),
      createLevelCompetency('lead-people', 'leading'),
      createLevelCompetency('scope-complexity', 'leading'),
      createLevelCompetency('impact-delivery', 'leading'),
      createLevelCompetency('impact-business', 'leading'),
      createLevelCompetency('comm-written', 'leading'),
      createLevelCompetency('comm-verbal', 'leading'),
    ],
  },
  {
    id: generateId(),
    code: 'M3',
    title: 'Director of Engineering',
    track: 'management',
    order: 3,
    typicalYearsExperience: '10+',
    scope: 'Department',
    impact: 'Department/business unit outcomes',
    responsibilities: [
      'Lead engineering for business area',
      'Set technical vision and strategy',
      'Build and scale engineering organization',
      'Partner with executives on company strategy',
    ],
    progressionCriteria: [
      'Delivers major business initiatives',
      'Builds high-performing engineering culture',
      'Attracts and retains top talent',
      'Recognized leader across company',
    ],
    competencies: [
      createLevelCompetency('tech-architecture', 'leading'),
      createLevelCompetency('lead-mentoring', 'defining'),
      createLevelCompetency('lead-influence', 'defining'),
      createLevelCompetency('lead-people', 'defining'),
      createLevelCompetency('scope-complexity', 'defining'),
      createLevelCompetency('impact-delivery', 'defining'),
      createLevelCompetency('impact-business', 'defining'),
      createLevelCompetency('comm-written', 'defining'),
      createLevelCompetency('comm-verbal', 'defining'),
    ],
  },
  {
    id: generateId(),
    code: 'M4',
    title: 'VP of Engineering',
    track: 'management',
    order: 4,
    typicalYearsExperience: '12+',
    scope: 'Organization',
    impact: 'Company-wide engineering',
    responsibilities: [
      'Lead all engineering functions',
      'Set company-wide engineering strategy',
      'Build and develop engineering leadership',
      'Partner with CEO and board on technology',
    ],
    progressionCriteria: [
      'Engineering enables business success',
      'World-class engineering organization',
      'Industry recognition for engineering culture',
      'Strong executive leadership presence',
    ],
    competencies: [
      createLevelCompetency('tech-architecture', 'defining'),
      createLevelCompetency('lead-mentoring', 'defining'),
      createLevelCompetency('lead-influence', 'defining'),
      createLevelCompetency('lead-people', 'defining'),
      createLevelCompetency('scope-complexity', 'defining'),
      createLevelCompetency('impact-delivery', 'defining'),
      createLevelCompetency('impact-business', 'defining'),
      createLevelCompetency('comm-written', 'defining'),
      createLevelCompetency('comm-verbal', 'defining'),
    ],
  },
];

export const SPOTIFY_TEMPLATE: LadderTemplate = {
  id: 'spotify',
  name: 'Spotify Model',
  company: 'Spotify-style',
  description: 'Based on Spotify\'s engineering ladder with squads and tribes. Features 5 IC levels and 4 management levels with clear progression criteria.',
  framework: {
    name: 'Spotify Engineering Ladder',
    description: 'A modern engineering career framework inspired by Spotify\'s model, emphasizing autonomy, mastery, and purpose.',
    tracks: {
      ic: {
        name: 'Individual Contributor',
        description: 'Technical track for engineers who want to grow their technical impact without people management responsibilities.',
        levels: SPOTIFY_IC_LEVELS,
      },
      management: {
        name: 'Engineering Management',
        description: 'Leadership track for engineers who want to grow their impact through people management and organizational leadership.',
        levels: SPOTIFY_MANAGEMENT_LEVELS,
      },
    },
    competencies: STANDARD_COMPETENCIES,
  },
};

// ============================================================================
// Google-Style Template
// ============================================================================

const GOOGLE_IC_LEVELS: Level[] = [
  {
    id: generateId(),
    code: 'L3',
    title: 'Software Engineer II',
    track: 'ic',
    order: 1,
    typicalYearsExperience: '0-2',
    scope: 'Task/Component',
    impact: 'Individual contributions',
    responsibilities: [
      'Write and test code for assigned tasks',
      'Debug issues in existing code',
      'Learn codebase and best practices',
      'Participate in code reviews',
    ],
    progressionCriteria: [
      'Delivers tasks consistently',
      'Code quality meets standards',
      'Growing independence',
      'Positive team feedback',
    ],
    competencies: [
      createLevelCompetency('tech-coding', 'learning'),
      createLevelCompetency('tech-debugging', 'learning'),
      createLevelCompetency('scope-autonomy', 'learning'),
      createLevelCompetency('impact-delivery', 'learning'),
      createLevelCompetency('comm-written', 'learning'),
    ],
  },
  {
    id: generateId(),
    code: 'L4',
    title: 'Software Engineer III',
    track: 'ic',
    order: 2,
    typicalYearsExperience: '2-5',
    scope: 'Feature/Module',
    impact: 'Team features',
    responsibilities: [
      'Own features end-to-end',
      'Design and implement solutions',
      'Review code from peers',
      'On-call participation',
    ],
    progressionCriteria: [
      'Independently delivers features',
      'High-quality code reviews',
      'Contributes to design discussions',
      'Mentors L3 engineers',
    ],
    competencies: [
      createLevelCompetency('tech-coding', 'practicing'),
      createLevelCompetency('tech-architecture', 'learning'),
      createLevelCompetency('tech-debugging', 'practicing'),
      createLevelCompetency('lead-mentoring', 'learning'),
      createLevelCompetency('scope-autonomy', 'practicing'),
      createLevelCompetency('impact-delivery', 'practicing'),
      createLevelCompetency('comm-written', 'practicing'),
    ],
  },
  {
    id: generateId(),
    code: 'L5',
    title: 'Senior Software Engineer',
    track: 'ic',
    order: 3,
    typicalYearsExperience: '5-8',
    scope: 'Project/System',
    impact: 'Team outcomes',
    responsibilities: [
      'Lead technical projects',
      'Design system architecture',
      'Mentor and grow team members',
      'Drive quality and best practices',
    ],
    progressionCriteria: [
      'Leads complex projects',
      'Architecture decisions adopted',
      'Measurable impact on team velocity',
      'Recognized technical expertise',
    ],
    competencies: [
      createLevelCompetency('tech-coding', 'leading'),
      createLevelCompetency('tech-architecture', 'practicing'),
      createLevelCompetency('tech-debugging', 'leading'),
      createLevelCompetency('lead-mentoring', 'practicing'),
      createLevelCompetency('lead-influence', 'practicing'),
      createLevelCompetency('scope-autonomy', 'leading'),
      createLevelCompetency('scope-complexity', 'practicing'),
      createLevelCompetency('impact-delivery', 'leading'),
      createLevelCompetency('impact-business', 'practicing'),
      createLevelCompetency('comm-written', 'leading'),
      createLevelCompetency('comm-verbal', 'practicing'),
    ],
  },
  {
    id: generateId(),
    code: 'L6',
    title: 'Staff Software Engineer',
    track: 'ic',
    order: 4,
    typicalYearsExperience: '8-12',
    scope: 'Organization/Area',
    impact: 'Multiple teams',
    responsibilities: [
      'Drive technical strategy across teams',
      'Solve cross-cutting technical challenges',
      'Shape engineering culture and practices',
      'Represent engineering externally',
    ],
    progressionCriteria: [
      'Strategy adopted across org',
      'Solves org-level challenges',
      'Builds team capabilities',
      'Domain thought leader',
    ],
    competencies: [
      createLevelCompetency('tech-coding', 'leading'),
      createLevelCompetency('tech-architecture', 'leading'),
      createLevelCompetency('tech-debugging', 'leading'),
      createLevelCompetency('lead-mentoring', 'leading'),
      createLevelCompetency('lead-influence', 'leading'),
      createLevelCompetency('scope-autonomy', 'leading'),
      createLevelCompetency('scope-complexity', 'leading'),
      createLevelCompetency('impact-delivery', 'leading'),
      createLevelCompetency('impact-business', 'leading'),
      createLevelCompetency('comm-written', 'leading'),
      createLevelCompetency('comm-verbal', 'leading'),
    ],
  },
  {
    id: generateId(),
    code: 'L7',
    title: 'Senior Staff Software Engineer',
    track: 'ic',
    order: 5,
    typicalYearsExperience: '12-15',
    scope: 'Product Area',
    impact: 'Product line',
    responsibilities: [
      'Set technical direction for product area',
      'Drive major technical initiatives',
      'Build and scale engineering practices',
      'Industry thought leadership',
    ],
    progressionCriteria: [
      'Defines product area architecture',
      'Delivers transformative initiatives',
      'Scales practices across org',
      'Industry recognition',
    ],
    competencies: [
      createLevelCompetency('tech-coding', 'defining'),
      createLevelCompetency('tech-architecture', 'defining'),
      createLevelCompetency('tech-debugging', 'defining'),
      createLevelCompetency('lead-mentoring', 'defining'),
      createLevelCompetency('lead-influence', 'defining'),
      createLevelCompetency('scope-autonomy', 'defining'),
      createLevelCompetency('scope-complexity', 'defining'),
      createLevelCompetency('impact-delivery', 'defining'),
      createLevelCompetency('impact-business', 'defining'),
      createLevelCompetency('comm-written', 'defining'),
      createLevelCompetency('comm-verbal', 'defining'),
    ],
  },
  {
    id: generateId(),
    code: 'L8',
    title: 'Principal Engineer',
    track: 'ic',
    order: 6,
    typicalYearsExperience: '15+',
    scope: 'Company-wide',
    impact: 'Company technical direction',
    responsibilities: [
      'Set company technical vision',
      'Solve hardest technical challenges',
      'Drive company-wide initiatives',
      'External technical ambassador',
    ],
    progressionCriteria: [
      'Shapes company strategy',
      'Solves unprecedented challenges',
      'Creates competitive advantage',
      'Industry luminary',
    ],
    competencies: [
      createLevelCompetency('tech-coding', 'defining'),
      createLevelCompetency('tech-architecture', 'defining'),
      createLevelCompetency('tech-debugging', 'defining'),
      createLevelCompetency('lead-mentoring', 'defining'),
      createLevelCompetency('lead-influence', 'defining'),
      createLevelCompetency('scope-autonomy', 'defining'),
      createLevelCompetency('scope-complexity', 'defining'),
      createLevelCompetency('impact-delivery', 'defining'),
      createLevelCompetency('impact-business', 'defining'),
      createLevelCompetency('comm-written', 'defining'),
      createLevelCompetency('comm-verbal', 'defining'),
    ],
  },
];

const GOOGLE_MANAGEMENT_LEVELS: Level[] = [
  {
    id: generateId(),
    code: 'M1',
    title: 'Engineering Manager',
    track: 'management',
    order: 1,
    typicalYearsExperience: '6+',
    scope: 'Single team',
    impact: 'Team outcomes',
    responsibilities: [
      'Manage team of 5-10 engineers',
      'People development and growth',
      'Technical guidance and quality',
      'Delivery and execution',
    ],
    progressionCriteria: [
      'Team delivers consistently',
      'Team members grow',
      'High engagement scores',
      'Effective partnerships',
    ],
    competencies: [
      createLevelCompetency('tech-architecture', 'practicing'),
      createLevelCompetency('lead-mentoring', 'leading'),
      createLevelCompetency('lead-influence', 'practicing'),
      createLevelCompetency('lead-people', 'practicing'),
      createLevelCompetency('scope-complexity', 'practicing'),
      createLevelCompetency('impact-delivery', 'leading'),
      createLevelCompetency('impact-business', 'practicing'),
      createLevelCompetency('comm-written', 'leading'),
      createLevelCompetency('comm-verbal', 'leading'),
    ],
  },
  {
    id: generateId(),
    code: 'M2',
    title: 'Senior Engineering Manager',
    track: 'management',
    order: 2,
    typicalYearsExperience: '8+',
    scope: 'Multiple teams',
    impact: 'Area outcomes',
    responsibilities: [
      'Manage 2-4 teams or managers',
      'Technical strategy for area',
      'Organizational design',
      'Leadership development',
    ],
    progressionCriteria: [
      'Area exceeds goals',
      'Builds strong managers',
      'Drives significant improvements',
      'Cross-functional leadership',
    ],
    competencies: [
      createLevelCompetency('tech-architecture', 'leading'),
      createLevelCompetency('lead-mentoring', 'leading'),
      createLevelCompetency('lead-influence', 'leading'),
      createLevelCompetency('lead-people', 'leading'),
      createLevelCompetency('scope-complexity', 'leading'),
      createLevelCompetency('impact-delivery', 'leading'),
      createLevelCompetency('impact-business', 'leading'),
      createLevelCompetency('comm-written', 'leading'),
      createLevelCompetency('comm-verbal', 'leading'),
    ],
  },
  {
    id: generateId(),
    code: 'M3',
    title: 'Director',
    track: 'management',
    order: 3,
    typicalYearsExperience: '12+',
    scope: 'Product area',
    impact: 'Product outcomes',
    responsibilities: [
      'Lead engineering for product area',
      'Technical vision and strategy',
      'Build and scale organization',
      'Executive partnerships',
    ],
    progressionCriteria: [
      'Product area success',
      'World-class engineering org',
      'Attracts top talent',
      'Company-wide influence',
    ],
    competencies: [
      createLevelCompetency('tech-architecture', 'leading'),
      createLevelCompetency('lead-mentoring', 'defining'),
      createLevelCompetency('lead-influence', 'defining'),
      createLevelCompetency('lead-people', 'defining'),
      createLevelCompetency('scope-complexity', 'defining'),
      createLevelCompetency('impact-delivery', 'defining'),
      createLevelCompetency('impact-business', 'defining'),
      createLevelCompetency('comm-written', 'defining'),
      createLevelCompetency('comm-verbal', 'defining'),
    ],
  },
  {
    id: generateId(),
    code: 'M4',
    title: 'Senior Director / VP',
    track: 'management',
    order: 4,
    typicalYearsExperience: '15+',
    scope: 'Organization',
    impact: 'Business unit',
    responsibilities: [
      'Lead major engineering function',
      'Company-wide strategy input',
      'Executive team participation',
      'External representation',
    ],
    progressionCriteria: [
      'Business unit success',
      'Engineering excellence',
      'Industry leadership',
      'Board-level presence',
    ],
    competencies: [
      createLevelCompetency('tech-architecture', 'defining'),
      createLevelCompetency('lead-mentoring', 'defining'),
      createLevelCompetency('lead-influence', 'defining'),
      createLevelCompetency('lead-people', 'defining'),
      createLevelCompetency('scope-complexity', 'defining'),
      createLevelCompetency('impact-delivery', 'defining'),
      createLevelCompetency('impact-business', 'defining'),
      createLevelCompetency('comm-written', 'defining'),
      createLevelCompetency('comm-verbal', 'defining'),
    ],
  },
];

export const GOOGLE_TEMPLATE: LadderTemplate = {
  id: 'google',
  name: 'Google Model',
  company: 'Google-style',
  description: 'Based on Google\'s engineering levels (L3-L8 for IC, M1-M4 for management). Features detailed leveling with emphasis on scope and impact.',
  framework: {
    name: 'Google Engineering Ladder',
    description: 'A comprehensive engineering career framework inspired by Google\'s leveling system, with clear progression criteria at each level.',
    tracks: {
      ic: {
        name: 'Individual Contributor',
        description: 'Technical ladder for engineers who want to deepen their technical expertise and drive impact through technical contributions.',
        levels: GOOGLE_IC_LEVELS,
      },
      management: {
        name: 'Engineering Management',
        description: 'Management ladder for engineers who want to grow through people leadership and organizational development.',
        levels: GOOGLE_MANAGEMENT_LEVELS,
      },
    },
    competencies: STANDARD_COMPETENCIES,
  },
};

// ============================================================================
// Startup Template (Simpler)
// ============================================================================

const STARTUP_IC_LEVELS: Level[] = [
  {
    id: generateId(),
    code: 'E1',
    title: 'Engineer',
    track: 'ic',
    order: 1,
    typicalYearsExperience: '0-3',
    scope: 'Features',
    impact: 'Team delivery',
    responsibilities: [
      'Build and ship features',
      'Write tests and documentation',
      'Participate in on-call rotation',
      'Learn rapidly and adapt',
    ],
    progressionCriteria: [
      'Ships features independently',
      'Code requires minimal review cycles',
      'Handles on-call effectively',
      'Proactive learner',
    ],
    competencies: [
      createLevelCompetency('tech-coding', 'practicing'),
      createLevelCompetency('tech-debugging', 'practicing'),
      createLevelCompetency('scope-autonomy', 'practicing'),
      createLevelCompetency('impact-delivery', 'practicing'),
    ],
  },
  {
    id: generateId(),
    code: 'E2',
    title: 'Senior Engineer',
    track: 'ic',
    order: 2,
    typicalYearsExperience: '3-6',
    scope: 'Systems',
    impact: 'Product outcomes',
    responsibilities: [
      'Own entire systems',
      'Make architecture decisions',
      'Mentor engineers',
      'Drive technical quality',
    ],
    progressionCriteria: [
      'Systems run reliably',
      'Architecture decisions prove sound',
      'Team members grow under guidance',
      'Improves engineering practices',
    ],
    competencies: [
      createLevelCompetency('tech-coding', 'leading'),
      createLevelCompetency('tech-architecture', 'practicing'),
      createLevelCompetency('tech-debugging', 'leading'),
      createLevelCompetency('lead-mentoring', 'practicing'),
      createLevelCompetency('lead-influence', 'practicing'),
      createLevelCompetency('scope-autonomy', 'leading'),
      createLevelCompetency('impact-delivery', 'leading'),
      createLevelCompetency('impact-business', 'practicing'),
    ],
  },
  {
    id: generateId(),
    code: 'E3',
    title: 'Staff Engineer',
    track: 'ic',
    order: 3,
    typicalYearsExperience: '6+',
    scope: 'Company-wide',
    impact: 'Company technical direction',
    responsibilities: [
      'Set technical direction',
      'Solve hardest problems',
      'Scale engineering practices',
      'External representation',
    ],
    progressionCriteria: [
      'Technical vision adopted',
      'Solves critical challenges',
      'Builds engineering culture',
      'Industry recognition',
    ],
    competencies: [
      createLevelCompetency('tech-coding', 'defining'),
      createLevelCompetency('tech-architecture', 'defining'),
      createLevelCompetency('tech-debugging', 'defining'),
      createLevelCompetency('lead-mentoring', 'leading'),
      createLevelCompetency('lead-influence', 'defining'),
      createLevelCompetency('scope-autonomy', 'defining'),
      createLevelCompetency('scope-complexity', 'defining'),
      createLevelCompetency('impact-delivery', 'defining'),
      createLevelCompetency('impact-business', 'leading'),
    ],
  },
];

const STARTUP_MANAGEMENT_LEVELS: Level[] = [
  {
    id: generateId(),
    code: 'M1',
    title: 'Engineering Lead',
    track: 'management',
    order: 1,
    typicalYearsExperience: '4+',
    scope: 'Team',
    impact: 'Team success',
    responsibilities: [
      'Lead team of 3-6 engineers',
      'Ship products on time',
      'Hire and develop talent',
      'Technical decisions',
    ],
    progressionCriteria: [
      'Team ships consistently',
      'Team grows and stays',
      'Technical quality high',
      'Effective partnerships',
    ],
    competencies: [
      createLevelCompetency('tech-architecture', 'practicing'),
      createLevelCompetency('lead-mentoring', 'leading'),
      createLevelCompetency('lead-people', 'practicing'),
      createLevelCompetency('impact-delivery', 'leading'),
      createLevelCompetency('impact-business', 'practicing'),
      createLevelCompetency('comm-verbal', 'leading'),
    ],
  },
  {
    id: generateId(),
    code: 'M2',
    title: 'Head of Engineering',
    track: 'management',
    order: 2,
    typicalYearsExperience: '8+',
    scope: 'Engineering org',
    impact: 'Company success',
    responsibilities: [
      'Lead all engineering',
      'Technical strategy',
      'Build engineering culture',
      'Executive team member',
    ],
    progressionCriteria: [
      'Engineering enables company success',
      'Strong technical foundation',
      'Attracts great talent',
      'Trusted executive partner',
    ],
    competencies: [
      createLevelCompetency('tech-architecture', 'leading'),
      createLevelCompetency('lead-mentoring', 'defining'),
      createLevelCompetency('lead-influence', 'defining'),
      createLevelCompetency('lead-people', 'defining'),
      createLevelCompetency('scope-complexity', 'defining'),
      createLevelCompetency('impact-delivery', 'defining'),
      createLevelCompetency('impact-business', 'defining'),
      createLevelCompetency('comm-verbal', 'defining'),
    ],
  },
];

export const STARTUP_TEMPLATE: LadderTemplate = {
  id: 'startup',
  name: 'Startup Model',
  company: 'Early-stage startup',
  description: 'Simplified framework for early-stage startups with 3 IC levels and 2 management levels. Emphasizes flexibility and rapid growth.',
  framework: {
    name: 'Startup Engineering Ladder',
    description: 'A lean engineering career framework designed for early-stage startups where titles matter less than impact.',
    tracks: {
      ic: {
        name: 'Individual Contributor',
        description: 'Technical track focused on shipping features and building systems.',
        levels: STARTUP_IC_LEVELS,
      },
      management: {
        name: 'Engineering Leadership',
        description: 'Leadership track for those building and scaling engineering teams.',
        levels: STARTUP_MANAGEMENT_LEVELS,
      },
    },
    competencies: STANDARD_COMPETENCIES,
  },
};

// ============================================================================
// Export All Templates
// ============================================================================

export const LADDER_TEMPLATES: LadderTemplate[] = [
  SPOTIFY_TEMPLATE,
  GOOGLE_TEMPLATE,
  STARTUP_TEMPLATE,
];

/**
 * Get a template by ID
 */
export function getTemplateById(id: string): LadderTemplate | undefined {
  return LADDER_TEMPLATES.find(t => t.id === id);
}

/**
 * Get the standard competencies list
 */
export function getStandardCompetencies(): Competency[] {
  return STANDARD_COMPETENCIES;
}
